/**
 * EphemeralEffect (Foundry: rule-element/ephemeral-effect.ts): an Effect or Condition applied to
 * the origin or target for one roll of the selectors. Recorded with the strikes and statistics
 * it names, for #19 to apply during a Roll.
 */
import { parseAlterations, type ItemAlteration } from "../alterations";
import { testPredicate } from "../predicate";
import { resolveInjectedProperties } from "../resolve";
import type { EngineState } from "../state";
import type { RuleHandler, RuleInstance } from "./base";
import { isItemUuid } from "./choice-set";

interface EphemeralEffectData {
  affects: "target" | "origin";
  selectors: string[];
  uuid: string;
  adjustName: boolean;
  alterations: ItemAlteration[];
}

function parse(rule: RuleInstance): void {
  const s = rule.source;
  const affects = s.affects === undefined ? "target" : s.affects;
  if (affects !== "target" && affects !== "origin") return rule.fail(`affects: "${String(affects)}" is not target or origin`);
  const raw = s.selectors ?? s.selector;
  const selectors = (Array.isArray(raw) ? raw : [raw]).filter((x): x is string => typeof x === "string" && x.length > 0);
  if (selectors.length === 0) return rule.fail("must have at least one selector");
  if (typeof s.uuid !== "string" || !s.uuid) return rule.fail("uuid: must be a non-empty string");
  const { alterations, error } = parseAlterations(s.alterations);
  if (error) return rule.fail(error);
  rule.data = { affects, selectors, uuid: s.uuid, adjustName: s.adjustName !== false, alterations } satisfies EphemeralEffectData as unknown as Record<string, unknown>;
}

/**
 * Foundry registers in afterPrepareData and reads the synthetic at roll time; here statistics and
 * strikes gather their extras while they derive, so the offer is registered before them. Its
 * predicate is still tested lazily, with the roll's options.
 */
function beforePrepareData(rule: RuleInstance, state: EngineState): void {
  if (rule.ignored) return;
  const data = rule.data as unknown as EphemeralEffectData;
  const uuid = resolveInjectedProperties(rule, state, data.uuid, { warn: false });
  const name = isItemUuid(uuid) ? (state.entries.get(uuid)?.name ?? state.catalogue?.byUuid(uuid)?.n ?? null) : null;
  const parentOptions = rule.item.getRollOptions("parent");
  for (const raw of data.selectors) {
    const selector = resolveInjectedProperties(rule, state, raw);
    if (rule.ignored) return;
    const record = (state.synthetics.ephemeralEffects[selector] ??= { target: [], origin: [] });
    record[data.affects].push({
      uuid,
      affects: data.affects,
      predicate: structuredClone(rule.predicate),
      parentOptions,
      adjustName: data.adjustName,
      source: rule.item.uuid,
      sourceName: rule.item.name,
      name: name ? (data.adjustName ? `${name} (${rule.reducedLabel})` : name) : null,
      test: (options) => {
        if (!isItemUuid(uuid)) {
          rule.problems.push(`"${uuid}" does not look like a UUID`);
          return false;
        }
        const predicate = resolveInjectedProperties(rule, state, structuredClone(rule.predicate), { warn: false });
        return testPredicate(predicate, new Set([...options, ...parentOptions]));
      },
    });
  }
}

export const EphemeralEffectHandler: RuleHandler = { key: "EphemeralEffect", parse, beforePrepareData };
